// Batch Lifecycle Demo v2.1 — page wiring. `wire()` builds the whole page over a document (plant, detector, trend, dock,
// lens, minimap, Events tab, tour) and returns its parts without starting a frame loop; `boot()` adds the loop. The split
// is what makes the wiring testable in jsdom (__tests__/main.wiring.test.js).
// Spec: xl-specs/specs/2026-10-14-batch-demo-v2-1-guide-dock-design.md §2 (dock, welcome/return), §5 (flow), §8 (phones).
import { createModel, SIGNALS, SIGNAL_INDEX, CONSTANTS as C } from "./sim.js"
import { createHistory } from "./history.js"
import { createTrend } from "./chart.js"
import { createAudio, annunciateAlarms } from "./audio.js"
import { createTour, STORAGE_KEYS } from "./tour.js"
import { createUI } from "./ui.js"
import { stepsFor } from "./loop.js"
import { createDock, createDockGroup, chatPayload, SVG_PLANT } from "./dock.js"
import { createAlarmsView } from "./alarms-view.js"
import { createChatCues, visitorContext } from "./chat-cues.js"
import { createLens } from "./lens.js"
import { createMinimap } from "./minimap.js"
import { createDetector } from "./detect.js"
import { createEventsView } from "./events-view.js"
import { CHAPTERS } from "./chapters.js"

/** The detector samples at 1 Hz of plant time: every 10th model step at the 0.1 s base step, whatever the speed. */
export const DETECTOR_STRIDE = Math.round(1 / C.DT)
/** Chapter 7 ("Try to break it") puts the fault buttons in front of the visitor. */
export const BREAK_CHAPTER = CHAPTERS.findIndex((c) => c.id === "break")
// Three bubbles (kraftware-dev#120): the Guide (the tour), the Plant (operating it) and the chat widget's own button.
const GUIDE_TABS = ["guide"]
const PLANT_TABS = ["controls", "events", "alarms", "trends"]

/**
 * One dock-shaped facade over several docks, so the tour and the wiring can say dock.open("controls") or
 * dock.el("events") without knowing which bubble holds the tab. The first dock is the default (the Guide).
 */
export function dockRouter(docks) {
  const byTab = (t) => docks.find((d) => d.el(t))
  const openDock = () => docks.find((d) => d.isOpen())
  return {
    docks,
    open(tab) { const d = tab ? byTab(tab) : docks[0]; if (d) d.open(tab) },
    close() { for (const d of docks) d.close() },
    collapse() { (openDock() || docks[0]).collapse() },
    sheet(height) { (openDock() || docks[0]).sheet(height) },
    el(t) { if (t === "bar") return docks[0].el("bar"); const d = byTab(t); return d ? d.el(t) : null },
    isOpen: () => docks.some((d) => d.isOpen()),
    activeTab: () => (openDock() || docks[0]).activeTab(),
    onTab(fn) { for (const d of docks) d.onTab(fn) },
    onOpenChange(fn) { for (const d of docks) d.onOpenChange(fn) },
    setBar(b) { docks[0].setBar(b) },
    setChatContext(fn) { for (const d of docks) d.setChatContext(fn) },
    badge(n) { docks[docks.length - 1].badge(n) },
    destroy() { for (const d of docks) d.destroy() },
  }
}
// Host-maintained flag log for the trend (filled from BEFORE the backfill, so the 8 h of pre-roll is flagged too).
const FLAG_TYPES = new Set(["batch.start", "batch.complete", "fault.injected", "fault.cleared", "preact.trim", "wear.rebuild_due", "wear.rebuilt", "alarm.raised"])

/**
 * The dock button's badge. One number, so the rule has to say which story it tells:
 *  - dock closed, or open on Guide: the active alarm count (the plant's state is what matters there);
 *  - open on Events: 0 (the visitor is looking at the events; the host marks every new one seen as it arrives);
 *  - open on Controls or Chat: alarms + events detected since the Events tab was last looked at.
 */
export function badgeCount({ alarms = 0, unseen = 0, open = false, tab = "guide" } = {}) {
  if (open && tab === "events") return 0
  if (!open || tab === "guide") return alarms
  return alarms + unseen
}

/**
 * The plant before any page exists: the model, its history and the detector, with the listeners that must hear the plant
 * from BEFORE the backfill (the event count, the trend's flag log, the detector). `backfillSample` is what each backfill
 * step records (history.pushBackfill, the detector at 1 Hz); `sample` is the live-step equivalent.
 */
export function createPlant({ seed = 1 } = {}) {
  const model = createModel({ seed })
  const history = createHistory(SIGNALS.length)
  const detector = createDetector({ signals: SIGNALS, signalIndex: SIGNAL_INDEX })
  const flags = []
  // Every plant event this shift, from before the backfill: chapter 0's question ("how many events?") answers live.
  let eventCount = 0
  model.onEvent(() => { eventCount++ })
  model.onEvent((e) => {
    if (FLAG_TYPES.has(e.type) || (e.type === "dose.verify" && e.result !== "ok")) {
      flags.push(e)
      if (flags.length > 4000) flags.shift()
    }
  })
  // The detector hears the plant from BEFORE the backfill, so the shift the page opens on is already interpreted.
  model.onEvent((e) => detector.onPlantEvent(e))
  let stepN = 0
  return {
    model, history, detector, flags,
    events: () => eventCount,
    sample(t, snap) {
      history.push(t, snap)
      if (++stepN % DETECTOR_STRIDE === 0) detector.feed(t, snap)
    },
    // The backfill ends at t = 0; the history skips what its fine tiers would overwrite anyway (page load time).
    backfillSample(t, snap) {
      history.pushBackfill(t, snap, 0)
      if (++stepN % DETECTOR_STRIDE === 0) detector.feed(t, snap)
    },
  }
}

/**
 * @param doc       the page document (index.html markup)
 * @param win       its window
 * @param storage   localStorage-like ({getItem, setItem}); the welcome / return state lives here
 * @param seed      plant seed
 * @param backfillS seconds of history simulated before the first frame (the shift the trend and detector start with)
 * @param plant     a createPlant() whose backfill has already run (boot slices it behind the loading bar); else built here
 * @param make      factory overrides for tests: { trend(root, opts), lens(opts), minimap(root, opts) }
 */
export function wire({ doc, win, storage, seed = 1, backfillS = C.BACKFILL_S, plant = null, make = {} }) {
  const $ = (id) => doc.getElementById(id)
  if (!plant) { plant = createPlant({ seed }); plant.model.backfill(backfillS, plant.backfillSample) }
  const { model, history, detector, flags } = plant
  const sample = plant.sample

  // Everything that renders is built after the backfill: one render, and the Events tab starts with nothing unseen.
  const audio = createAudio({ mode: "none" })
  const trend = (make.trend || createTrend)($("trendRoot"), { history, events: flags, detected: detector.events })
  const ui = createUI({ model, trend, audio, doc })
  const group = createDockGroup()
  const guideDock = createDock($("dock"), { tabs: GUIDE_TABS, group,
    labels: { open: "Open the guided tour", close: "Close the guided tour", panel: "Guided tour", tabs: "Guided tour" } })
  const plantDock = createDock($("plantDock"), { name: "plant", tabs: PLANT_TABS, group, icon: SVG_PLANT,
    labels: { open: "Open the plant: controls, events, alarms and trends", close: "Close the plant panel", panel: "Plant", tabs: "Plant sections" } })
  const dock = dockRouter([guideDock, plantDock])
  const lens = make.lens ? make.lens({ doc, ui, history, detected: detector.events }) : createLens({ doc, ui, history, detected: detector.events })

  let tour = null
  // The lens as the visitor drives it (Events "Show me", a minimap tap): the tour's timed focus must not replace it.
  const visitorLens = {
    show(f) { if (tour) tour.holdFocus(); lens.show(f) },
    hide: () => lens.hide(), isOpen: () => lens.isOpen(), update: () => lens.update(),
  }
  const eventsView = createEventsView(dock.el("events"), { detector, lens: visitorLens, trend, now: () => model.t, doc })
  tour = createTour({ model, trend, lens, dock, audio, detector, storage, doc, eventsView, stats: { events: plant.events } })

  const minimapSlot = dock.el("trends") && dock.el("trends").querySelector('[data-slot="minimap"]')
  const minimap = minimapSlot ? (make.minimap || createMinimap)(minimapSlot, { history, detected: detector.events, trend }) : null
  if (minimap) minimap.onTap((p) => visitorLens.show({ target: "lane:wt", window: p.window }))
  // A pill on the trend opens its card in the Events tab and lights it up on the trend.
  if (trend.onEventClick) trend.onEventClick((ev) => {
    if (!ev) return
    dock.open("events"); eventsView.open(ev.id)
    if (trend.highlightEvent) trend.highlightEvent(ev.id)
  })

  // ---- Alarms tab: the full list with ACK (the header banner shows the newest few) ----
  const alarmsView = dock.el("alarms") ? createAlarmsView(dock.el("alarms"), { model, onAck: () => ui.render(), doc }) : null
  // ---- Trends tab: "show me the trend" leaves the panel for the page's own trend ----
  const toTrend = $("showTrendBtn")
  if (toTrend) toTrend.addEventListener("click", () => {
    plantDock.collapse()
    const tp = $("trendPanel"); if (tp && tp.scrollIntoView) tp.scrollIntoView({ block: "start", behavior: "smooth" })
  })

  // ---- badge (rule: badgeCount) — on the Plant button, which holds Events and Alarms ----
  let lastBadge = -1
  function updateBadge() {
    const open = plantDock.isOpen(), tab = plantDock.activeTab()
    if (open && tab === "events") eventsView.markSeen()
    const n = badgeCount({ alarms: model.alarms.size, unseen: eventsView.unseen(), open, tab })
    if (n !== lastBadge) { lastBadge = n; dock.badge(n) }
  }
  eventsView.onNew(updateBadge)
  dock.onTab(updateBadge)
  dock.onOpenChange(updateBadge)

  // ---- chat context: the chapter, the live alarms and the last three detected events, as the visitor sees them ----
  function chatContext() {
    const ch = tour.mode === "tour" ? CHAPTERS[tour.chapterIdx] : null
    return {
      chapterTitle: ch ? ch.title : "",
      alarms: Array.from(model.alarms.values()).map((a) => a.text),
      events: detector.events.slice(-3).map((e) => e.interpretation).filter(Boolean),
    }
  }
  // What the visitor did, in their words-ish: the chat agent gets the last few with the context.
  const actions = []
  const did = (a) => { actions.push(a); if (actions.length > 6) actions.shift(); publishContext() }
  function fullContext() {
    const c = chatContext()
    return visitorContext({ ...c, events: detector.events, actions, now: model.t })
  }
  function publishContext() {
    const p = chatPayload(chatContext())
    // The widget sends __xlVisitorContext with every message, so this is how the event queue reaches the agent whenever
    // the visitor opens the chat (their interest); it is refreshed on every chapter, detection and action.
    win.__xlVisitorContext = fullContext()
    win.__xlVideoGreeting = p.customGreeting
  }
  dock.setChatContext(chatContext)
  const quiet = () => tour.speaking() || !!doc.querySelector('[aria-modal="true"]:not([hidden]).open, #welcomeOverlay:not([hidden])')
  const chat = createChatCues({ doc, win, context: fullContext, quiet })
  win.__xlChat = { open: chat.open }
  // A tour answer such as "Ask the engineering agent" opens the chat bubble, which is stepping away from the tour: pause it.
  // (__xlChat.open({ reason }) is the hook for a future inference trigger.)
  tour.openChat = (o) => { toChat(); chat.open(o) }
  if (detector.onDetect) detector.onDetect((ev, info) => { if (info && info.updated) return; publishContext(); chat.note("detected", ev) })
  if (eventsView.onOpen) eventsView.onOpen((ev) => { did(`opened event "${String(ev.interpretation || ev.class).slice(0, 40)}"`); chat.note("eventOpened", ev) })

  // ---- three buttons (operator 2026-10-16): the Guide (its own pane), the Plant (Controls / Events / Alarms / Trends) and
  // the chat. Stepping away from the tour (the Plant or the chat button, or closing the Guide) pauses it mid-sentence; the
  // Guide button then reads "▶ <chapter>", and one tap carries on from the same word. The page opening a pane for the tour
  // (chapter 7's Controls, "I'll do it") is the tour's own move and pauses nothing.
  function syncGuideFab() {
    if (guideDock.isOpen()) guideDock.setFab(null)   // open: the round close button
    else {
      const r = tour.resumeTarget()
      guideDock.setFab(r.kind === "start" ? { label: "Start the tour", title: "Start the guided tour" }
        : { label: r.title, title: `Resume the guided tour at ${r.title}` })
    }
    const fab = $("dockFab"); if (fab) fab.classList.toggle("dock-fab-attn", !!tour.attention && !guideDock.isOpen())
  }
  guideDock.onUser((kind) => {
    if (kind === "close") tour.pause("closed")
    else if (kind === "open") {
      // Inside the visitor's tap: an iPhone lets the narration carry on only from one.
      if (tour.mode === "tour") tour.resume()
      else if (tour.storedAudio) tour.start(tour.storedAudio)   // carries on at the remembered chapter
      else tour.showWelcome()
    }
  })
  plantDock.onUser((kind) => { if (kind === "open") tour.pause("plant") })
  // The chat is the third pane: the panes take turns, so opening it closes the Guide / Plant pane (and the Guide button
  // shows "▶ <chapter>" for the way back). Its button and the cue's Ask live outside the docks (cs.kraftware.dev's widget).
  function toChat() { tour.pause("chat"); dock.close() }
  doc.addEventListener("click", (e) => {
    const t = e.target
    if (t && t.closest && t.closest("#xlcs-btn, .chat-cue-ask")) toChat()
  }, true)
  dock.onOpenChange(syncGuideFab)

  // ---- chapter changes: bar title, visitor context, chapter 7's Controls tab ----
  let shownIdx = null, bridged = false, resumeRow = null
  tour.onChapter((t) => {
    syncGuideFab()
    const idx = t.mode === "tour" ? t.chapterIdx : null
    dock.setBar({ title: idx == null ? "Guide" : CHAPTERS[idx].title })
    if (idx !== shownIdx || idx === BREAK_CHAPTER) {
      bridged = false
      // Entering chapter 7: the fault buttons in view (desktop panel; phone half sheet). Leaving it for another chapter:
      // back to the bubble. A stopped tour leaves the dock where the visitor put it.
      if (idx === BREAK_CHAPTER) dock.open("controls")
      else if (shownIdx === BREAK_CHAPTER && idx != null) dock.open("guide")
      shownIdx = idx
    }
    if (idx != null && resumeRow) { resumeRow.remove(); resumeRow = null }
    if (idx === CHAPTERS.length - 1) chat.note("tourEnd")
    publishContext()
  })
  // In chapter 7 a fault picked from the Controls tab is the chapter's answer: the bubble reveals it (and stops the slow
  // path moving on while the visitor watches). ui.js's own button handler has already injected the fault by the time this
  // ancestor listener runs, so the answer's own inject is a no-op (injectFault ignores an active fault).
  const faultPanel = $("faultPanel")
  if (faultPanel) faultPanel.addEventListener("click", (e) => {
    const f = e.target.closest && e.target.closest("[data-fault]")
    if (f) { did(`injected fault ${f.dataset.fault}`); chat.note("fault", { fault: f.dataset.fault }) }
  })
  if (faultPanel) faultPanel.addEventListener("click", (e) => {
    const b = e.target.closest && e.target.closest("[data-fault]")
    if (!b || bridged || tour.mode !== "tour" || tour.chapterIdx !== BREAK_CHAPTER) return
    if (doc.querySelector("#gbAnswers .gb-answer.chosen")) return   // already answered in the bubble: leave the tab alone
    const j = CHAPTERS[BREAK_CHAPTER].answers.findIndex((a) => a.action === `inject:${b.dataset.fault}`)
    if (j < 0) return
    bridged = true
    dock.open("guide")   // first: the bubble scrolls its reveal into view only in a visible pane (the phone sheet)
    tour.answer(j)
  })

  // ---- speed / pause / restart ----
  const st = { speed: 8, running: true, carry: 0 }
  ui.build()
  const speedSel = $("simSpeedSelect"); if (speedSel) speedSel.value = String(st.speed)
  ui.onSpeed((s) => { st.speed = s })
  ui.onPlayPause(() => {
    st.running = !st.running
    const ic = $("playPauseIcon"), lb = $("playPauseLabel")
    if (ic) ic.textContent = st.running ? "⏸" : "▶"
    if (lb) lb.textContent = st.running ? "Running" : "Paused"
  })
  ui.onRestart(() => model.restart())

  // ---- welcome / return ----
  const safeGet = (k) => { try { return storage.getItem(k) } catch (_) { return null } }
  const returning = safeGet(STORAGE_KEYS.seen) === "1"
  model.onEvent((e) => tour.handleEvent(e))
  annunciateAlarms(model, audio)   // after the backfill, like the tour: the shift of history never rings
  tour.init()                      // first visit: the welcome overlay; its tour choices open the dock on Guide (tour.start)
  const free = $("welcomeFree"); if (free) free.addEventListener("click", () => dock.close())
  if (returning) {
    // Return visit (spec §2): no banner; the dock button pulses once and the Guide tab opens with "Resume the tour".
    // The click is the user gesture that may apply the stored sound choice (consent gate): audio stays opt-in.
    const host = dock.el("guide").querySelector(".dock-slot") || dock.el("guide")
    resumeRow = doc.createElement("div"); resumeRow.className = "resume-row"; resumeRow.id = "resumeRow"
    const sound = { narrated: "narration on", silent: "sounds only", none: "silent" }[tour.storedAudio || "none"]
    const hi = doc.createElement("span"); hi.className = "resume-hi"; hi.textContent = `Welcome back · ${sound}`
    const r = tour.resumeTarget()
    const chip = doc.createElement("button"); chip.type = "button"; chip.id = "resumeTourBtn"; chip.className = "chip resume-chip"
    chip.textContent = r.chapterIdx > 0 ? `▶ Resume · ${r.title}` : "▶ Resume the tour"
    chip.addEventListener("click", () => tour.start(tour.storedAudio || "none"))   // start() carries on at the remembered chapter
    resumeRow.append(hi, chip)
    host.insertBefore(resumeRow, host.firstChild)
    const fab = $("dockFab")
    if (fab) { fab.classList.add("dock-fab-pulse"); fab.addEventListener("animationend", () => fab.classList.remove("dock-fab-pulse"), { once: true }) }
  }
  publishContext()
  updateBadge()
  syncGuideFab()

  // ---- per frame ----
  function advance(steps) {
    for (let i = 0; i < steps; i++) { model.step(); sample(model.t, model.snapshot()) }
  }
  function render(first = false) {
    ui.render()
    if (alarmsView) alarmsView.render()
    lens.update()
    updateBadge()
    trend.render(model.t, first)
  }
  function tick(elapsedMs) {
    if (st.running) {
      const r = stepsFor(elapsedMs, st.speed, st.carry); st.carry = r.carry
      advance(r.steps)
    }
    render()
  }
  function destroy() {
    if (minimap && minimap.destroy) minimap.destroy()
    lens.hide()
    eventsView.destroy()
    if (alarmsView) alarmsView.destroy()
    chat.destroy()
    dock.destroy()
  }
  return { model, history, detector, flags, trend, ui, dock, guideDock, plantDock, lens, visitorLens, eventsView, alarmsView, chat, tour, minimap, audio, state: st,
    advance, render, tick, updateBadge, chatContext, fullContext, publishContext, destroy }
}

/** Longest stretch the sliced backfill holds the main thread before it lets the page paint (the loading bar). */
export const SLICE_BUDGET_MS = 40

/** Give the browser a turn (paint, input) between slices: scheduler.yield, else a message-channel task. Never rAF (paused in a background tab). */
export function yieldToPage(win) {
  try { if (win.scheduler && typeof win.scheduler.yield === "function") return win.scheduler.yield() } catch (_) { /* fall through */ }
  return new Promise((resolve) => {
    try { const ch = new win.MessageChannel(); ch.port1.onmessage = () => { ch.port1.close(); resolve() }; ch.port2.postMessage(0) }
    catch (_) { setTimeout(resolve, 0) }
  })
}

/**
 * The backfill in time-boxed slices (same steps as model.backfill), reporting the fraction done after each, so the
 * loading bar shows the 8 h being simulated instead of a frozen page (it is ~1 s of CPU on a laptop, several on a phone).
 */
export async function backfillSliced(plant, seconds, { win, onProgress = () => {}, budgetMs = SLICE_BUDGET_MS } = {}) {
  const now = () => (win && win.performance ? win.performance.now() : Date.now())
  const it = plant.model.backfillSlices(seconds, plant.backfillSample, 500)
  let t0 = now()
  for (const f of it) {
    if (now() - t0 < budgetMs && f < 1) continue
    onProgress(f)
    if (f >= 1) break
    await yieldToPage(win)
    t0 = now()
  }
}

/**
 * The live page: simulate the shift behind the loading bar, wire everything, paint the trend once, then run the frame
 * loop. `loader` is index.html's window.__xlLoader (step / progress); the auto-boot below reports ready or the failure.
 */
export async function boot(doc, win, { loader = null } = {}) {
  let storage
  try { storage = win.localStorage } catch (_) { storage = null }
  if (!storage) storage = { getItem: () => null, setItem: () => {} }
  const seed = Number(new URLSearchParams(win.location.search).get("seed") || 1)
  const plant = createPlant({ seed })
  if (loader) loader.step("history")
  await backfillSliced(plant, C.BACKFILL_S, { win, onProgress: (f) => { if (loader) loader.progress("history", f) } })
  if (loader) loader.step("draw")
  await yieldToPage(win)   // the bar shows "Drawing the trend" before the first render takes the thread
  const page = wire({ doc, win, storage, seed, plant })
  page.render(true)
  try { win.performance.mark("xl:first-trend-paint") } catch (_) { /* no User Timing */ }
  let last = win.performance.now()
  function frame(now) {
    const elapsed = now - last; last = now
    page.tick(elapsed)
    win.requestAnimationFrame(frame)
  }
  win.requestAnimationFrame(frame)
  win.__xlBatchDemo = page   // for the console and the smoke script
  return page
}

/** The page's auto-start: boot behind index.html's loader, then hand it ready, or the reason the demo could not start. */
export function autoBoot(doc, win) {
  const loader = win.__xlLoader || null
  if (loader) loader.step("code")
  return boot(doc, win, { loader }).then((page) => { if (loader) loader.ready(); return page }, (err) => {
    if (loader) loader.fail("the demo could not start (" + String((err && err.message) || err) + ")")
    console.error("batch demo boot", err)
    throw err
  })
}

if (typeof document !== "undefined" && typeof window !== "undefined" && document.getElementById("trendRoot") && !window.__XL_NO_BOOT) autoBoot(document, window).catch(() => {})
